import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { OpenMarketOptions } from '../components/upsell-feedback/UpsellFeedback';
import { LatestPeriodSequenceV2, getLatestSequencePeriodForSubscription } from '../services/Market.service';

export type UpsellSlideDataType = {
  cell: string;
  deliveriesPerYear: number;
  dataAccessEnd: string;
  latestPublished: string;
};

export const useUpsellAlert = (cells: string[], openMarketsOptions: OpenMarketOptions[]): any => {
  const [isMounted, setIsMounted] = useState<boolean>(true);
  const [selectedCells, setSelectedCells] = useState<string[]>([]);
  const [selectedOpenMarketsOptions, setSelectedOpenMarketsOptions] = useState<OpenMarketOptions[]>([]);
  const [isCampaignsDisabled, setIsCampaignsDisabled] = useState<boolean>(false);
  const [upsellNudgeDataForSlide, setUpsellNudgeDataForSlide] = useState<UpsellSlideDataType[]>();
  const [latestSequenceData4x6x, setLatestSequenceData4x6x] = useState<LatestPeriodSequenceV2[]>(undefined);
  const [showUpsell, setShowUpsell] = useState<boolean>(false);

  const { isAuthenticated, getAccessToken } = useAuthentication();

  useEffect(() => {
    setSelectedCells(cells);
    setSelectedOpenMarketsOptions(openMarketsOptions);
    setShowUpsell(false);
    setUpsellNudgeDataForSlide(undefined);

    const disableCookie = document.cookie
      .split('; ')
      .filter(row => row.startsWith('disable_campaigns='))
      .map(c => c.split('=')[1])[0];
    setIsCampaignsDisabled(!!disableCookie);
  }, [cells, openMarketsOptions]);

  useEffect(() => {
    (async () => {
      const token = await getAccessToken();
      const result = await getLatestSequencePeriodForSubscription(token);
      if (isMounted) {
        setLatestSequenceData4x6x(result);
      }
    })();

    return () => {
      setIsMounted(false);
    };
    // disabling exhaustive deps to prevent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkLastSequencePeriod = useCallback(() => {
    if (
      Array.isArray(selectedOpenMarketsOptions) &&
      Array.isArray(selectedCells) &&
      selectedCells.length &&
      Array.isArray(latestSequenceData4x6x) &&
      latestSequenceData4x6x.length
    ) {
      const latestSequenceValue = latestSequenceData4x6x.map(sequence => {
        const selectedMarketPeriods = selectedOpenMarketsOptions.find(
          market => market.value === sequence.cell
        )?.periods;
        const selectedMarketPeriod = selectedMarketPeriods?.find(period => period.periodicity === sequence.periodicity);
        return {
          cell: sequence.cell,
          deliveries: sequence.deliveries,
          countryName: selectedOpenMarketsOptions.find(market => market.value === sequence.cell)?.countryName,
          productName: selectedOpenMarketsOptions.find(market => market.value === sequence.cell)?.productName,
          dataAccessEndDatePeriodSequence: selectedMarketPeriod?.sequence,
          latestPublishedPeriodSequence: sequence?.latestPublishedPeriodSequence,
          dataAccessEndDatePeriodSequenceDisplay: selectedMarketPeriod?.name,
          latestPublishedPeriodSequenceDisplay: sequence?.latestPublishedPeriodSequenceDisplay,
        };
      });
      const selectedHasOutdatedData = latestSequenceValue
        .filter(sequence => selectedCells.includes(sequence.cell))
        .some(value => Number(value.latestPublishedPeriodSequence) > Number(value.dataAccessEndDatePeriodSequence));
      setShowUpsell(selectedHasOutdatedData);
      return latestSequenceValue;
    }
    return [];
  }, [latestSequenceData4x6x, selectedCells, selectedOpenMarketsOptions]);

  useEffect(() => {
    if (!isAuthenticated || isCampaignsDisabled) {
      return;
    }
    const upsellDataForModal = checkLastSequencePeriod();
    if (showUpsell) {
      const upsellData: UpsellSlideDataType[] = upsellDataForModal
        .sort(
          (a, b) =>
            Number(Number(a.dataAccessEndDatePeriodSequence) < Number(a.latestPublishedPeriodSequence)) -
            Number(Number(b.dataAccessEndDatePeriodSequence) > Number(b.latestPublishedPeriodSequence))
        )
        .sort((a, b) => Number(selectedCells.includes(b.cell)) - Number(selectedCells.includes(a.cell)))
        .map(cell => ({
          cell: `${cell.countryName} - ${cell.productName}`,
          deliveriesPerYear: cell.deliveries,
          dataAccessEnd: cell.dataAccessEndDatePeriodSequenceDisplay,
          latestPublished: cell.latestPublishedPeriodSequenceDisplay,
        }));
      setUpsellNudgeDataForSlide(upsellData);
    }
  }, [checkLastSequencePeriod, isAuthenticated, isCampaignsDisabled, showUpsell, selectedCells]);

  return useMemo(
    () => ({
      showUpsell,
      upsellNudgeDataForSlide,
    }),
    [showUpsell, upsellNudgeDataForSlide]
  );
};
